import { useEffect, useRef } from "react";
import type { BossRevealCircleVariant, Point } from "../types";

const PARTICLE_COUNT = 320;

const VERTEX_SHADER = `
precision highp float;
attribute vec4 a_particle;
attribute vec2 a_phase;
uniform vec2 u_resolution;
uniform vec2 u_anchor;
uniform float u_radius;
uniform float u_time;
uniform float u_duration;
uniform float u_variant;
uniform float u_dpr;
varying float v_alpha;
varying float v_heat;
void main() {
  float timeline = clamp(u_time / u_duration, 0.0, 1.0);
  float life = fract(timeline * (1.15 + a_particle.z * 0.22) + a_phase.x);
  float fade = smoothstep(0.0, 0.11, life) * (1.0 - smoothstep(0.72, 1.0, life));
  float globalFade = smoothstep(0.0, 0.07, timeline) * (1.0 - smoothstep(0.82, 1.0, timeline));
  float angle;
  float radius;
  if (u_variant < 0.5) {
    angle = a_particle.x + u_time * (0.8 + a_particle.z * 1.3) + life * 4.2;
    radius = u_radius * (0.55 + a_particle.y * 2.5 + sin(life * 6.283 + a_phase.y) * 0.22);
  } else if (u_variant < 1.5) {
    angle = a_particle.x - u_time * (1.1 + a_particle.z * 1.6) + life * 7.0;
    radius = u_radius * (3.4 - life * 3.0) * (0.55 + a_particle.y * 0.55);
  } else {
    angle = a_particle.x + sin(u_time * 1.7 + a_phase.y) * 0.28;
    radius = u_radius * (0.25 + life * (2.5 + a_particle.y * 2.3));
  }
  vec2 position = u_anchor + vec2(cos(angle), sin(angle)) * radius;
  vec2 clip = position / u_resolution * 2.0 - 1.0;
  clip.y *= -1.0;
  gl_Position = vec4(clip, 0.0, 1.0);
  gl_PointSize = a_particle.w * u_dpr * (0.65 + (1.0 - life) * 0.8);
  v_alpha = fade * globalFade * (0.42 + a_particle.y * 0.58);
  v_heat = clamp((1.0 - life) * 0.75 + a_particle.z * 0.25, 0.0, 1.0);
}`;

const FRAGMENT_SHADER = `
precision mediump float;
uniform vec3 u_color;
varying float v_alpha;
varying float v_heat;
void main() {
  vec2 point = gl_PointCoord - vec2(0.5);
  float distanceToCenter = length(point) * 2.0;
  float core = 1.0 - smoothstep(0.0, 0.34, distanceToCenter);
  float glow = 1.0 - smoothstep(0.18, 1.0, distanceToCenter);
  float alpha = (core + glow * 0.72) * v_alpha;
  vec3 hot = mix(u_color, vec3(1.0, 0.94, 0.78), core * (0.45 + v_heat * 0.55));
  gl_FragColor = vec4(hot * (0.72 + glow * 0.55), alpha);
}`;

function seedFromId(id: string) {
  let hash = 2166136261;
  for (let index = 0; index < id.length; index += 1) { hash ^= id.charCodeAt(index); hash = Math.imul(hash, 16777619); }
  return hash >>> 0;
}

export function createBossParticleData(id: string, count = PARTICLE_COUNT) {
  let seed = seedFromId(id) || 1;
  const random = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
  const data = new Float32Array(count * 6);
  for (let index = 0; index < count; index += 1) {
    const offset = index * 6;
    data[offset] = random() * Math.PI * 2; data[offset + 1] = random(); data[offset + 2] = random();
    data[offset + 3] = 3.5 + random() * 7.5; data[offset + 4] = random(); data[offset + 5] = random() * Math.PI * 2;
  }
  return data;
}

function compile(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type); if (!shader) return null;
  gl.shaderSource(shader, source); gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) { gl.deleteShader(shader); return null; }
  return shader;
}

function colorChannels(hex: string) {
  const normalized = /^#[0-9a-f]{6}$/i.test(hex) ? hex.slice(1) : "b83cff";
  return [Number.parseInt(normalized.slice(0, 2), 16) / 255, Number.parseInt(normalized.slice(2, 4), 16) / 255, Number.parseInt(normalized.slice(4, 6), 16) / 255] as const;
}

const VARIANT_INDEX: Record<BossRevealCircleVariant, number> = { runic: 0, void: 1, infernal: 2 };

export function BossRevealParticles({ effectId, variant, color, anchor, width, height, radius, startedAt, durationMs, paused = false }: { effectId: string; variant: BossRevealCircleVariant; color: string; anchor: Point; width: number; height: number; radius: number; startedAt: number; durationMs: number; paused?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas || width <= 0 || height <= 0) return;
    const gl = canvas.getContext("webgl", { alpha: true, antialias: false, depth: false, premultipliedAlpha: true, powerPreference: "high-performance" });
    if (!gl) { canvas.dataset.webglReady = "false"; return; }
    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER); const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertex || !fragment) { canvas.dataset.webglReady = "false"; return; }
    const program = gl.createProgram(); if (!program) return;
    gl.attachShader(program, vertex); gl.attachShader(program, fragment); gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) { canvas.dataset.webglReady = "false"; gl.deleteProgram(program); return; }
    const dpr = Math.min(1.6, window.devicePixelRatio || 1); canvas.width = Math.max(1, Math.round(width * dpr)); canvas.height = Math.max(1, Math.round(height * dpr));
    gl.viewport(0, 0, canvas.width, canvas.height); gl.useProgram(program); gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE); gl.disable(gl.DEPTH_TEST);
    const buffer = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buffer); gl.bufferData(gl.ARRAY_BUFFER, createBossParticleData(`${effectId}:${variant}`), gl.STATIC_DRAW);
    const stride = 6 * Float32Array.BYTES_PER_ELEMENT; const particle = gl.getAttribLocation(program, "a_particle"); const phase = gl.getAttribLocation(program, "a_phase");
    gl.enableVertexAttribArray(particle); gl.vertexAttribPointer(particle, 4, gl.FLOAT, false, stride, 0); gl.enableVertexAttribArray(phase); gl.vertexAttribPointer(phase, 2, gl.FLOAT, false, stride, 4 * Float32Array.BYTES_PER_ELEMENT);
    const resolution = gl.getUniformLocation(program, "u_resolution"); const anchorLocation = gl.getUniformLocation(program, "u_anchor"); const radiusLocation = gl.getUniformLocation(program, "u_radius");
    const time = gl.getUniformLocation(program, "u_time"); const duration = gl.getUniformLocation(program, "u_duration"); const variantLocation = gl.getUniformLocation(program, "u_variant"); const dprLocation = gl.getUniformLocation(program, "u_dpr"); const colorLocation = gl.getUniformLocation(program, "u_color");
    gl.uniform2f(resolution, width, height); gl.uniform2f(anchorLocation, anchor.x, anchor.y); gl.uniform1f(radiusLocation, radius); gl.uniform1f(duration, durationMs / 1_000); gl.uniform1f(variantLocation, VARIANT_INDEX[variant]); gl.uniform1f(dprLocation, dpr); gl.uniform3fv(colorLocation, colorChannels(color));
    canvas.dataset.webglReady = "true"; let active = true; let released = false; let frame: number | undefined;
    const draw = () => {
      if (!active) return; const elapsed = Math.max(0, Date.now() - startedAt);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); gl.uniform1f(time, Math.min(durationMs, elapsed) / 1_000); gl.drawArrays(gl.POINTS, 0, PARTICLE_COUNT);
      if (!paused && elapsed < durationMs) frame = requestAnimationFrame(draw);
    };
    draw();
    const release = () => {
      if (released) return;
      released = true; active = false;
      if (frame !== undefined) cancelAnimationFrame(frame);
      gl.deleteBuffer(buffer); gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
    window.addEventListener("pagehide", release);
    return () => { window.removeEventListener("pagehide", release); release(); };
  }, [anchor.x, anchor.y, color, durationMs, effectId, height, paused, radius, startedAt, variant, width]);
  return <canvas ref={canvasRef} className="boss-reveal-webgl" data-boss-webgl-particles="true" data-webgl-ready="pending" data-particle-count={PARTICLE_COUNT} data-particle-variant={variant} aria-hidden="true" />;
}
